import React from 'react';
import { Skeleton } from '@mui/material';
import Tooltip from 'src/components/Tooltip';
import { currencyConvertor } from 'src/utils/currencyConvertor';
import { addCommasToNumber, getCompanyLogo } from '../../../utils/utilityMethods';
import { capitalizeFirstLetter, capitalizeFirstLetterInAllWords } from 'src/utils/utilityMethods';
import cx from 'classnames';
import MillerHeimanRole from 'src/components/MillerHeimanRole';
import { AiQuestionToolTipTheme, icpFitmentTooltipTheme } from 'src/model/componentTypes';
import Badge from 'src/components/Badge';
import AIFundingContainer from '../../AIFundingDetailsContainer';
import AiQuestionContainer from 'src/components/AIQuestionContainer';
import Markdown from 'react-markdown';
import AvatarGroup from '../../AvatarGroup';
import Avatar from '../../Avatar';
import { filterProfilesByName, getNamesOnly } from 'src/utils/getProflleValuesByArray';
import ConnectionPathDrawer from 'src/components/ConnectionPathDrawer';
import AccountLogo from 'src/assets/icons/account-logo.svg';
import { timezoneConverter } from 'src/utils/timezonesConvertor';

interface IAddressFieldProps {
  isLoading: boolean;
  value: string;
}

export const AddressField = React.memo(({ isLoading, value }: IAddressFieldProps) => {
  if (isLoading) {
    return (
      <div className="truncation-fields address-field">
        <Skeleton width={120} className="skeleton-styling" />
      </div>
    );
  }
  if (value === 'N/A') return <span className="empty-default-text">{value}</span>;
  return (
    <Tooltip title={(value || '').replace(/,/g, ', ')} placement="top">
      <div className="truncation-fields address-field">
        {(value || '').length > 18
          ? `${(value || '').replace(/,/g, ', ').substring(0, 18)}...`
          : (value || '').replace(/,/g, ', ')}
      </div>
    </Tooltip>
  );
});

interface IFieldProps {
  isLoading: boolean;
  value: string;
}

export const NumberField = React.memo(({ isLoading, value }: IFieldProps) => {
  if (isLoading) {
    return (
      <div className="normal-fields">
        <Skeleton width={70} className="skeleton-styling" />
      </div>
    );
  }
  if (value === 'N/A') return <span className="empty-default-text">{value}</span>;

  return (
    <div className="normal-fields">
      {typeof value === 'number' ? addCommasToNumber(value) : value}
    </div>
  );
});

export const NormalField = React.memo(({ isLoading, value }: IFieldProps) => {
  if (isLoading) {
    return (
      <div className="normal-fields">
        <Skeleton width={70} className="skeleton-styling" />
      </div>
    );
  }
  if (value === 'N/A' || typeof value !== 'string')
    return <span className="empty-default-text">{value}</span>;
  return (
    <>
      {value?.length > 30 ? (
        <Tooltip title={value || ''} placement="top">
          <div className="normal-fields">{(value || '').substring(0, 30)}...</div>
        </Tooltip>
      ) : (
        <div className="normal-fields">
          {typeof value === 'string' &&
          (value?.toLowerCase() === 'b2b' || value?.toLowerCase() === 'b2c')
            ? value.toUpperCase()
            : value}
        </div>
      )}
    </>
  );
});

export const RangeField = React.memo(({ isLoading, value }: IFieldProps) => {
  if (isLoading) {
    return (
      <div className="et-range-fields">
        <Skeleton width={90} className="skeleton-styling" />
      </div>
    );
  }
  if (value === 'N/A') return <span className="empty-default-text">{value}</span>;
  return <div className="et-range-fields">{value}</div>;
});

export const TruncatedField = React.memo(({ isLoading, value }: IFieldProps) => {
  if (isLoading) {
    return (
      <div className="truncation-fields">
        <Skeleton width={120} className="skeleton-styling" />
      </div>
    );
  }
  if (value === 'N/A') return <span className="empty-default-text">{value}</span>;
  if ((value || '').length > 20) {
    return (
      <Tooltip title={(value || '').replace(/,/g, ', ')} placement="top">
        <div className="truncation-fields">
          {`${(value || '').replace(/,/g, ', ').substring(0, 20)}...`}
        </div>
      </Tooltip>
    );
  }
  return <div className="normal-fields">{value}</div>;
});

export const BooleanField = React.memo(({ isLoading, value }: IFieldProps) => {
  if (isLoading) {
    return <Skeleton width={70} className="skeleton-styling" />;
  }
  if (value === 'N/A') return <span className="empty-default-text">{value}</span>;
  return <>{`${value}`}</>;
});

export const DateField = React.memo(({ isLoading, value }: IFieldProps) => {
  if (isLoading) {
    return <Skeleton width={100} className="skeleton-styling" />;
  }
  if (value === 'N/A') return <span className="empty-default-text">{value}</span>;
  return <> {value} </>;
});

interface ILinkFieldProps {
  isLoading: boolean;
  value: string;
  link: string;
}
export const LinkField = React.memo(({ isLoading, value, link }: ILinkFieldProps) => {
  if (isLoading) {
    return (
      <div className="truncation-fields ">
        <Skeleton width={70} className="skeleton-styling" />
      </div>
    );
  }
  if (value === 'N/A') return <span className="empty-default-text">{value}</span>;
  return (
    <a href={link} target="_blank" rel="noreferrer" className="truncation-fields ">
      <Tooltip
        title={
          <a href={link} target="_blank" rel="noreferrer">
            {(value || '').replace(/,/g, ', ')}
          </a>
        }
        placement="top"
      >
        <span>
          {(value || '').length > 20
            ? `${(value || '').replace(/,/g, ', ').substring(0, 20)}...`
            : (value || '').replace(/,/g, ', ')}
        </span>
      </Tooltip>
    </a>
  );
});

interface ICampaignStatesProps {
  isLoading: boolean;
  campaignStages: any;
  value: string;
}
export const CampaignStages = React.memo(
  ({ isLoading, campaignStages, value }: ICampaignStatesProps) => {
    if (isLoading) {
      return <Skeleton width={80} className="skeleton-styling" />;
    }
    if (value === 'N/A') return <span className="empty-default-text">{value}</span>;
    return (
      <p
        className={`enhanced-table-campaign-stages enhanced-table-campaign-stage-${
          campaignStages[value] ? campaignStages[value] : 'Orange'
        }`}
      >
        {capitalizeFirstLetterInAllWords(value)}
      </p>
    );
  },
);

export const CapitalizeFirstLetterWrapper = React.memo(({ isLoading, value }: IFieldProps) => {
  if (isLoading) {
    return <Skeleton width={60} className="skeleton-styling" />;
  }
  if (value === 'N/A') return <span className="empty-default-text">{value}</span>;
  return <> {capitalizeFirstLetter(`${value}`)}</>;
});

export const ArrayValuesWrapper = React.memo(({ isLoading, value }: IFieldProps) => {
  if (isLoading) {
    return <Skeleton width={100} height={20} />;
  }

  if (!value || value === 'N/A') {
    return <span className="empty-default-text">N/A</span>;
  }

  return (
    <div className="array-values">
      {Array.isArray(value)
        ? value.map((item, index) => (
            <span key={index} className="array-item">
              {capitalizeFirstLetter(item)}
              {index < value.length - 1 && ', '}
            </span>
          ))
        : capitalizeFirstLetter(value)}
    </div>
  );
});

export const defaultValue = (
  row: any,
  field: any,
  category: string | null,
  subCategory: string | null,
  timezone?: string,
) => {
  if (row?.[field] === undefined) return 'N/A';
  if (
    (field === 'lastModifiedDate' || field === 'noOfVisits') &&
    (row[field] === '' || row[field] === null)
  ) {
    return 'Not Visited';
  }
  if (
    (field === 'lastModifiedDate' || field === 'latestFundingDate' || field === 'jobChangeDate' || field === 'changeDetectedDate') &&
    row[field] != '' &&
    row[field] != null
  ) {
    return timezoneConverter(row[field], timezone ? timezone : '');
  }
  if (
    (field === 'noOfVisits' || field === 'noOfEmployees' || subCategory === 'Number') &&
    row[field] !== null &&
    row[field] !== 'null' &&
    row[field] !== ''
  ) {
    return addCommasToNumber(row[field]);
  }
  if (
    (field === 'totalFundingAmount' ||
      field === 'latestFundingAmount' ||
      subCategory === 'Amount') &&
    row[field] !== null &&
    row[field] !== 'null' &&
    row[field] !== ''
  ) {
    return currencyConvertor(row[field], 2) || 0;
  }
  if (field === 'revenue' && row[field] !== null && row[field] !== 'null' && row[field] !== '') {
    return currencyConvertor(row[field], 0) || 0;
  }
  if (row[field] !== null && row[field] !== 'null' && row[field] !== '') {
    if (row?.accountSource === 'Sprouts DB' || row?.contactSource === 'Sprouts DB') {
      if (field === 'title') {
        return row[field];
      }
      if (category === 'Account Custom Fields' || category === 'Contact Custom Fields') {
        return row[field];
      }
      if (field === 'icpFitmentScore' || field === 'icpContributingFactors') {
        return row[field];
      }
      return capitalizeFirstLetterInAllWords(`${row[field] || ''}`);
    } else {
      return row[field];
    }
  }

  return field === 'intentTopics' ||
    field === 'intentScore' ||
    field === 'tags' ||
    field === 'campaignStage' ||
    field === 'owner'
    ? 'N/A'
    : 'N/A';
};

export const getLinks = (value: any) => {
  const isShortLink =
    /^[-a-zA-Z0-9@:%._~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_.~#?&//=]*)$/.test(
      value,
    );
  const isLongLink =
    /(http|ftp|https):\/\/([\w_-]+(?:(?:\.[\w_-]+)+))([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])/.test(
      value,
    );
  let link = value;
  if (isShortLink && value[0] + value[1] + value[2] !== 'www') {
    link = 'http://www.' + value;
  }
  const isEmail = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(value);
  if (isEmail) {
    link = 'mailto:' + value;
  }
  return { isShortLink, isLongLink, link, isEmail };
};

export const MillerHeimanData = React.memo(
  ({ role, description, loading }: { role: string; description: string; loading: boolean }) => {
    const badgeClasses = cx({
      'badge-fitment': true,
      'badge-fitment-high': ['Economic Buyer', 'Functional Buyer', 'Technical Buyer'].includes(
        role?.trim(),
      ),
      'badge-fitment-low': ['Others'].includes(role?.trim()),
      'badge-fitment-medium': [
        'Technical Influencer',
        'Functional Influencer',
        'Economic Influencer',
      ].includes(role?.trim()),
    });
    if (role && role !== 'N/A') {
      return loading ? (
        <Skeleton width={60} className="skeleton-styling" />
      ) : (
        <Tooltip
          title={
            <>
              <MillerHeimanRole role={role} description={description}></MillerHeimanRole>
            </>
          }
          placement="bottom"
          istheme={icpFitmentTooltipTheme}
          PopperProps={{
            disablePortal: true,
            sx: {
              '&[data-popper-reference-hidden]': {
                visibility: 'hidden',
                pointerEvents: 'none',
              },
            },
          }}
          arrow
        >
          <div className="miller-heiman-badge">
            <Badge className={badgeClasses} content={role} />
          </div>
        </Tooltip>
      );
    }
    return loading ? (
      <Skeleton width={60} className="skeleton-styling" />
    ) : (
      <span className="empty-default-text">N/A</span>
    );
  },
);

export const AISignalCell = React.memo(
  ({
    label,
    message,
    sourceUrls,
    loading,
    useCssTruncation = false,
  }: {
    loading: boolean;
    label: string;
    message: string;
    sourceUrls: string[];
    useCssTruncation?: boolean;
  }) => {
    if (message && message !== 'N/A') {
      return loading ? (
        <Skeleton width={60} className="skeleton-styling" />
      ) : (
        <Tooltip
          title={
            <>
              <AIFundingContainer
                label={label}
                message={message}
                sourceUrls={sourceUrls}
              ></AIFundingContainer>
            </>
          }
          placement="bottom"
          istheme={icpFitmentTooltipTheme}
          PopperProps={{
            disablePortal: true,
            sx: {
              '&[data-popper-reference-hidden]': {
                visibility: 'hidden',
                pointerEvents: 'none',
              },
            },
          }}
          arrow
        >
          <div className={`tal-ai-signal${useCssTruncation ? ' tal-ai-signal--css-truncate' : ''}`}>
            {useCssTruncation
              ? message
              : message.length > 25
              ? `${message.substring(0, 25)}...`
              : message}
          </div>
        </Tooltip>
      );
    }
    return loading ? (
      <Skeleton width={60} className="skeleton-styling" />
    ) : (
      <span className="empty-default-text">N/A</span>
    );
  },
);

export const AiQuestionContent = React.memo(
  ({ loading, value, useCssTruncation = false }: { loading: boolean; value: string; useCssTruncation?: boolean }) => {
    if (loading) {
      return <Skeleton width={120} className="skeleton-styling" />;
    }
    const newValArr = value
      ?.replaceAll('n -', ' \n-')
      .replaceAll('n-', ' \n-')
      .replaceAll('**n', '**')
      .split('A :');
    const newVal = newValArr?.[1] || '';
    let question = newValArr?.[0]?.split('Q :')[1] || '';
    question = capitalizeFirstLetter(question.trim());

    const content = (
      <Tooltip
        istheme={AiQuestionToolTipTheme}
        title={<AiQuestionContainer value={newVal} />}
        placement="bottom"
      >
        <div className={useCssTruncation ? 'ai-question-content--css-truncate' : undefined}>
          <Markdown className="question-markdown">
            {useCssTruncation
              ? question
              : question?.length > 30
              ? question.substring(0, 30).trim() + '...'
              : question}
          </Markdown>
        </div>
      </Tooltip>
    );
    return content;
  },
);

export const ConnectedPathData = React.memo(
  ({
    loading,
    value,
    row,
    rowIndex,
    data,
  }: {
    loading: boolean;
    value: any;
    row: any;
    rowIndex: number;
    data: any;
  }) => {
    const profilepicresp = filterProfilesByName(value);
    const namesOnlyResp = getNamesOnly(profilepicresp);
    const [selectedConnectedRow, setSelectedConnectedRow] = React.useState<number>(0);
    const [connectedDrawerOpen, setConnectedDrawerOpen] = React.useState(false);
    const handleConnectedPathPicClick = (rowId: number) => {
      setSelectedConnectedRow(rowId);
      setConnectedDrawerOpen(true);
    };
    if (profilepicresp.length > 0) {
      if (loading) {
        return <Skeleton width={60} className="skeleton-styling" />;
      } else {
        return (
          <>
            <Tooltip title={namesOnlyResp} placement="bottom">
              <div>
                <AvatarGroup max={4} onClick={() => handleConnectedPathPicClick(rowIndex)}>
                  {profilepicresp.map((item, index) => (
                    <Avatar key={index} src={item.src} name={item.name} />
                  ))}
                </AvatarGroup>
              </div>
            </Tooltip>
            <div>
              <ConnectionPathDrawer
                open={connectedDrawerOpen}
                setOpen={() => setConnectedDrawerOpen(false)}
                row={row}
                profilesData={profilepicresp}
                selectedIndex={selectedConnectedRow}
                data={data}
              />
            </div>
          </>
        );
      }
    } else {
      return <></>;
    }
  },
);

export const SubAccountName = React.memo(
  ({ loading, domainValue, row }: { loading: boolean; row: any; domainValue: string }) => {
    const [isAccountCompanyLogoError, setIsAccountCompanyLogoError] = React.useState(false);

    return (
      <div className="target-sub-account-name">
        {loading ? (
          <Skeleton
            variant="rounded"
            width={28}
            height={28}
            className="target-profiles-checkboxes skelton-checkbox-table"
          />
        ) : (
          row?.subAccountName && (
            <div className="target-sub-account-contact-avatar">
              <img
                onError={(e) => {
                  const logoUrl = getCompanyLogo(domainValue, row?.companyLogoUrl || '');
                  // Only set error if we got a valid URL but it failed to load
                  if (logoUrl) {
                    e.currentTarget.src = AccountLogo;
                  } else {
                    // If URL is invalid, directly use AccountLogo without showing error state
                    e.currentTarget.src = AccountLogo;
                    setIsAccountCompanyLogoError(true);
                  }
                }}
                className={`account-logo ${!isAccountCompanyLogoError && 'account-logo-bordered'}`}
                src={
                  isAccountCompanyLogoError
                    ? AccountLogo
                    : getCompanyLogo(domainValue, row?.companyLogoUrl || '') || AccountLogo
                }
                alt={domainValue}
              />
            </div>
          )
        )}
        <NormalField
          value={row?.subAccountName ? row?.subAccountName : 'N/A'}
          isLoading={loading}
        />
      </div>
    );
  },
);

interface IntegratedDataCellProps {
  value: string;
  loading: boolean;
}
export const IntegratedDataCell = ({ value, loading }: IntegratedDataCellProps) => {
  if (loading) {
    return (
      <div className="integrated-data-field">
        <Skeleton width={120} className="skeleton-styling" />
      </div>
    );
  }

  if (value === 'N/A') {
    return (
      <div className="integrated-data-field">
        <div className="integrated-data-field-calculating skeleton-animation">Calculating...</div>
      </div>
    );
  }

  return <div className="integrated-data-field">{addCommasToNumber(value)}</div>;
};
